import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import Swal from 'sweetalert2'
import Select from 'react-select'
import type { TenantThemeConfig } from '../types/theme'
import * as authService from '../services/authService'
import { completeRegistration, checkUsername, checkMobileAvailability, checkBankAccountAvailability, normalizeThaiMobile } from '../services/registrationService'
import { fetchAvailableBanks, toBankOptions, type BankOption } from '../services/bankService'
import {
  clearRegisterCompleteFlowState,
  isValidModernRegisterCompleteFlowState,
  resolveModernRegisterCompleteFlowState,
  type ModernRegisterCompleteFlowState,
} from '@huay/shared/registerFlowState'

type CompletePageState = ModernRegisterCompleteFlowState

interface ModernRegisterCompletePageProps {
  themeConfig: TenantThemeConfig | null
}

// react-select: แสดงไอคอนธนาคารใน dropdown และช่องที่เลือก (เหมือน user-classic)
const CustomBankOption = (props: { data: BankOption; innerProps: React.HTMLAttributes<HTMLDivElement>; innerRef: React.Ref<HTMLDivElement>; isSelected: boolean; isFocused: boolean }) => {
  const { data, innerProps, innerRef, isSelected, isFocused } = props
  return (
    <div
      ref={innerRef}
      {...innerProps}
      style={{
        ...(innerProps.style as React.CSSProperties),
        display: 'flex',
        alignItems: 'center',
        padding: '10px 15px',
        cursor: 'pointer',
        backgroundColor: isSelected ? '#e3f2fd' : isFocused ? '#f5f5f5' : 'white',
        borderBottom: '1px solid #eee',
      }}
    >
      {data.imageUrl && (
        <img
          src={data.imageUrl}
          alt={data.label}
          style={{ width: 24, height: 24, marginRight: 12, objectFit: 'contain', borderRadius: 4 }}
        />
      )}
      <span style={{ fontSize: 14, color: '#333', fontWeight: isSelected ? 500 : 400 }}>{data.label}</span>
    </div>
  )
}

const CustomBankSingleValue = (props: { data: BankOption }) => {
  const { data } = props
  return (
    <div style={{ display: 'flex', alignItems: 'center', fontSize: 14, color: '#333', height: '100%', lineHeight: 1 }}>
      {data.imageUrl && (
        <img
          src={data.imageUrl}
          alt={data.label}
          style={{ width: 20, height: 20, marginRight: 8, objectFit: 'contain', borderRadius: 3, verticalAlign: 'middle', display: 'inline-block' }}
        />
      )}
      <span style={{ verticalAlign: 'middle', display: 'inline-block', lineHeight: 20 }}>{data.label}</span>
    </div>
  )
}

const bankSelectStyles: Record<string, unknown> = {
  container: (base: Record<string, unknown>) => ({ ...base, width: '100%', height: 45, minHeight: 0 }),
  control: (base: Record<string, unknown>, state: { isFocused: boolean }) => ({
    ...base,
    border: '1px solid #ced4da',
    borderRadius: 8,
    minHeight: 45,
    height: 45,
    maxHeight: 45,
    backgroundColor: 'white',
    boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(0, 123, 255, 0.25)' : 'none',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
  }),
  placeholder: (base: Record<string, unknown>) => ({
    ...base,
    color: '#6c757d',
    fontSize: 14,
    margin: 0,
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
  }),
  input: (base: Record<string, unknown>) => ({
    ...base,
    margin: 0,
    padding: 0,
    height: '100%',
    display: 'flex',
    alignItems: 'center',
  }),
  menu: (base: Record<string, unknown>) => ({
    ...base,
    zIndex: 9999,
    borderRadius: 8,
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    border: '1px solid #e0e0e0',
    marginTop: 4,
    minWidth: 300,
    width: 'auto',
  }),
  menuList: (base: Record<string, unknown>) => ({ ...base, padding: '4px 0', maxHeight: 200 }),
  option: (base: Record<string, unknown>, state: { isSelected: boolean; isFocused: boolean }) => ({
    ...base,
    backgroundColor: state.isSelected ? '#e3f2fd' : state.isFocused ? '#f5f5f5' : 'white',
    color: '#333',
    fontSize: 14,
    padding: '10px 15px',
  }),
  singleValue: (base: Record<string, unknown>) => ({
    ...base,
    color: '#333',
    fontSize: 14,
    margin: 0,
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
  }),
  indicatorSeparator: () => ({ display: 'none' }),
  valueContainer: (base: Record<string, unknown>) => ({
    ...base,
    display: 'flex',
    alignItems: 'center',
    padding: '0 8px',
    height: '100%',
    flexWrap: 'nowrap',
    position: 'relative',
  }),
}

/**
 * หน้า Complete การสมัคร (STEP 3 ตาม register1.html)
 * รองรับ 2 แบบ: มาจาก OTP (จาก user-classic flow) → กรอกรหัสผ่าน + ข้อมูลบัญชี | มาจาก passcode (เดิม) → ใช้รหัสจาก state
 */
const ModernRegisterCompletePage: React.FC<ModernRegisterCompletePageProps> = ({ themeConfig }) => {
  const location = useLocation()
  const navigate = useNavigate()
  const flowState = useMemo(
    () => resolveModernRegisterCompleteFlowState(location.state as CompletePageState | null),
    [location.state]
  )

  const fromOtp = flowState?.fromOtp === true
  const fromPasscode = flowState?.fromPasscode === true

  const phone = flowState?.phone ? normalizeThaiMobile(flowState.phone) : ''
  const sessionId = flowState?.sessionId ?? ''
  const passwordFromState = flowState?.password ?? ''

  const [usernameInput, setUsernameInput] = useState(() =>
    flowState?.username && flowState.username.trim() ? flowState.username.trim() : ''
  )
  const [fullName, setFullName] = useState('')
  const [bankCode, setBankCode] = useState('')
  const [accountNumber, setAccountNumber] = useState('')
  const [passwordInput, setPasswordInput] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [fullNameError, setFullNameError] = useState('')
  const [bankError, setBankError] = useState('')
  const [accountError, setAccountError] = useState('')
  const [passwordError, setPasswordError] = useState('')
  const [confirmPasswordError, setConfirmPasswordError] = useState('')
  const [usernameError, setUsernameError] = useState('')
  const [isCheckingUsername, setIsCheckingUsername] = useState(false)
  const [usernameAvailable, setUsernameAvailable] = useState<boolean | null>(null)
  const [usernameSuggestions, setUsernameSuggestions] = useState<string[]>([])
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [bankOptions, setBankOptions] = useState<BankOption[]>([])
  const [bankImageUrl, setBankImageUrl] = useState('')
  const [isLoadingBanks, setIsLoadingBanks] = useState(false)
  const [mobileTaken, setMobileTaken] = useState(false)
  const [bankAccountTaken, setBankAccountTaken] = useState(false)
  const [bankAccountCheckMessage, setBankAccountCheckMessage] = useState('')
  const [isCheckingBankAccount, setIsCheckingBankAccount] = useState(false)

  const username = (usernameInput && usernameInput.trim()) ? usernameInput.trim().toLowerCase() : phone

  // เมื่อรหัสผ่านกับยืนยันรหัสผ่านตรงกัน (เหมือน user-classic)
  const confirmPasswordMatch =
    !!passwordInput &&
    !!confirmPassword &&
    passwordInput === confirmPassword &&
    !fromPasscode

  useEffect(() => {
    if (authService.isAuthenticated()) {
      navigate('/', { replace: true })
      return
    }
  }, [navigate])

  // ห้ามเข้าหน้านี้โดยไม่ผ่าน confirm OTP — ต้องมี state จากหน้า register หลังยืนยัน OTP (หรือจาก passcode)
  useEffect(() => {
    if (!isValidModernRegisterCompleteFlowState(flowState)) {
      clearRegisterCompleteFlowState()
      navigate('/register', { replace: true })
    }
  }, [flowState, navigate])

  // ตรวจสอบชื่อผู้ใช้แบบ debounce (เหมือน user-classic)
  const checkUsernameAvailability = useCallback(async (raw: string) => {
    if (!themeConfig?.tenantId || !raw || raw.trim().length < 6) {
      setUsernameAvailable(null)
      setUsernameSuggestions([])
      setShowSuggestions(false)
      return
    }
    if (!/^[a-zA-Z0-9]+$/.test(raw.trim())) {
      setUsernameAvailable(false)
      setUsernameSuggestions([])
      setShowSuggestions(false)
      return
    }
    setIsCheckingUsername(true)
    setUsernameAvailable(null)
    try {
      const res = await checkUsername(themeConfig.tenantId, raw.trim())
      setUsernameAvailable(res.available === true ? true : false)
      setUsernameSuggestions(res.suggestions || [])
      setShowSuggestions(!res.available && (res.suggestions?.length ?? 0) > 0)
    } catch {
      setUsernameAvailable(false)
      setUsernameSuggestions([])
      setShowSuggestions(false)
    } finally {
      setIsCheckingUsername(false)
    }
  }, [themeConfig?.tenantId])

  useEffect(() => {
    if (!usernameInput || usernameInput.trim().length < 6) {
      setUsernameAvailable(null)
      setUsernameSuggestions([])
      setShowSuggestions(false)
      return
    }
    const t = setTimeout(() => {
      checkUsernameAvailability(usernameInput)
    }, 500)
    return () => clearTimeout(t)
  }, [usernameInput, checkUsernameAvailability])

  // โหลดรายการธนาคารจาก tenant/API (เหมือน user-classic)
  useEffect(() => {
    const tid = themeConfig?.tenantId
    if (!tid) return
    let cancelled = false
    setIsLoadingBanks(true)
    fetchAvailableBanks(tid)
      .then((banks) => {
        if (!cancelled) setBankOptions(toBankOptions(banks))
      })
      .catch(() => {
        if (!cancelled) setBankOptions([])
      })
      .finally(() => {
        if (!cancelled) setIsLoadingBanks(false)
      })
    return () => {
      cancelled = true
    }
  }, [themeConfig?.tenantId])

  // เช็คเบอร์โทรว่าสมัครแล้วหรือยัง (ซ้ำในระบบ = disable ปุ่ม)
  useEffect(() => {
    const tid = themeConfig?.tenantId
    if (!tid || !phone) return
    let cancelled = false
    checkMobileAvailability(tid, phone).then((r) => {
      if (!cancelled) setMobileTaken(r.success && !r.available)
    })
    return () => { cancelled = true }
  }, [themeConfig?.tenantId, phone])

  // เช็คคู่ธนาคาร+หมายเลขบัญชีว่าถูกใช้แล้วหรือยัง (debounce)
  useEffect(() => {
    const tid = themeConfig?.tenantId
    const code = (bankCode || '').trim()
    const num = (accountNumber || '').replace(/\D/g, '')
    if (!tid || !code || num.length < 10 || num.length > 12) {
      setBankAccountTaken(false)
      setBankAccountCheckMessage('')
      setBankError('')
      return
    }
    let cancelled = false
    setIsCheckingBankAccount(true)
    const t = setTimeout(() => {
      checkBankAccountAvailability(tid, code, num).then((r) => {
        if (!cancelled) {
          const taken = r.success && !r.available
          setBankAccountTaken(taken)
          const msg = taken ? (r.message || 'เลขบัญชีนี้มีผู้ใช้งานแล้วในระบบ') : ''
          setBankAccountCheckMessage(msg)
          if (taken) setBankError(msg)
          else setBankError('')
        }
        if (!cancelled) setIsCheckingBankAccount(false)
      })
    }, 500)
    return () => {
      cancelled = true
      clearTimeout(t)
    }
  }, [themeConfig?.tenantId, bankCode, accountNumber])

  const validate = (): boolean => {
    let ok = true
    if (fromOtp) {
      if (!usernameInput || usernameInput.trim().length < 6) {
        setUsernameError('ยูสเซอร์ต้องมีอย่างน้อย 6 อักษร')
        ok = false
      } else if (!/^[a-zA-Z0-9]+$/.test(usernameInput.trim())) {
        setUsernameError('ใช้ได้เฉพาะภาษาอังกฤษและตัวเลขเท่านั้น')
        ok = false
      } else if (usernameAvailable === false) {
        setUsernameError('ยูสเซอร์นี้มีผู้ใช้งานแล้ว')
        ok = false
      } else if (isCheckingUsername) {
        setUsernameError('กำลังตรวจสอบยูสเซอร์...')
        ok = false
      } else {
        setUsernameError('')
      }
      if (!passwordInput || passwordInput.length < 6) {
        setPasswordError('รหัสผ่านต้องมีอย่างน้อย 6 ตัวอักษร')
        ok = false
      } else {
        setPasswordError('')
      }
      if (passwordInput !== confirmPassword) {
        setConfirmPasswordError('รหัสผ่านไม่ตรงกัน')
        ok = false
      } else {
        setConfirmPasswordError('')
      }
    }
    if (!fullName.trim()) {
      setFullNameError('กรุณากรอกชื่อ-นามสกุล')
      ok = false
    } else {
      setFullNameError('')
    }
    if (!bankCode || !bankCode.trim()) {
      setBankError('กรุณาเลือกธนาคาร')
      ok = false
    } else {
      setBankError('')
    }
    const acc = accountNumber.replace(/\D/g, '')
    if (!acc || acc.length < 10) {
      setAccountError('กรุณากรอกหมายเลขบัญชี (ตัวเลขอย่างน้อย 10 หลัก)')
      ok = false
    } else {
      setAccountError('')
    }
    if (mobileTaken) ok = false
    if (bankAccountTaken) {
      setBankError(bankAccountCheckMessage || 'เลขบัญชีนี้มีผู้ใช้งานแล้วในระบบ')
      ok = false
    }
    return ok
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const pwd = fromOtp ? passwordInput : passwordFromState
    if (!themeConfig?.tenantId || !phone || !sessionId || !pwd) {
      navigate('/register', { replace: true })
      return
    }
    if (!validate()) return

    setSubmitting(true)
    try {
      const referralCode =
        typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('affiliate_referral_code') : null

      const result = await completeRegistration(
        themeConfig.tenantId,
        phone,
        pwd,
        sessionId,
        username,
        {
          fullName: fullName.trim(),
          bankCode: bankCode.trim(),
          accountNumber: accountNumber.replace(/\D/g, '').trim(),
          ...(bankImageUrl.trim() ? { bankImageUrl: bankImageUrl.trim() } : {}),
          ...(referralCode && referralCode.trim() ? { referralCode: referralCode.trim() } : {}),
        }
      )
      if (result.success) {
        clearRegisterCompleteFlowState()
        if (referralCode) {
          try {
            sessionStorage.removeItem('affiliate_referral_code')
          } catch {
            /* ignore */
          }
        }
        const loginRes = await authService.login({
          tenantId: themeConfig.tenantId,
          username,
          password: pwd,
          rememberMe: false,
        })
        if (loginRes.success) {
          await Swal.fire({
            icon: 'success',
            title: 'สมัครสมาชิกสำเร็จ',
            html: 'กำลังนำคุณเข้าสู่ระบบ',
            timer: 2000,
            showConfirmButton: false,
            timerProgressBar: true,
          })
          navigate('/dashboard', { replace: true })
        } else {
          await Swal.fire({
            icon: 'success',
            title: 'สมัครสมาชิกสำเร็จ',
            text: 'กรุณาเข้าสู่ระบบด้วยเบอร์โทร/ชื่อผู้ใช้และรหัสผ่านที่ตั้งไว้',
            confirmButtonText: 'เข้าสู่ระบบ',
          })
          navigate('/login', { replace: true })
        }
      } else {
        await Swal.fire({
          icon: 'error',
          title: 'สมัครสมาชิกไม่สำเร็จ',
          text: result.message || 'กรุณาลองใหม่อีกครั้ง',
        })
      }
    } catch (e) {
      await Swal.fire({
        icon: 'error',
        title: 'เกิดข้อผิดพลาด',
        text: (e as Error)?.message ?? 'กรุณาลองใหม่อีกครั้ง',
      })
    } finally {
      setSubmitting(false)
    }
  }

  if (!themeConfig || !isValidModernRegisterCompleteFlowState(flowState)) {
    return (
      <div className="wrap_login d-flex align-items-center justify-content-center" style={{ minHeight: '100vh' }}>
        <div className="c_tertiary">กำลังโหลด...</div>
        <div className="bg_show" />
      </div>
    )
  }

  const logoUrl = themeConfig.theme?.branding?.logoUrl

  // โครงสร้างตรงตาม register1.html — ใช้ class register-complete-wrap + CSS ใน index.css บังคับความกว้าง
  return (
    <div className="wrap_login register-complete-wrap">
      <div className="my-3 my-md-5 w-100">
        <div className="container-fluid px-2 px-md-3">
          <div className="row mx__1 g-0 justify-content-center">
            <div className="col-12 register-complete-card-wrap px-2 px-md-3">
              <div className="bg_op_secondary border_10 p-3 p-md-5 light_sd bdc_primary">
                <div className="img_logo_l">
                  {logoUrl ? (
                    <img src={logoUrl} alt="" />
                  ) : (
                    <span className="c_tertiary font_md">Logo</span>
                  )}
                </div>

                <div className="c_tertiary text-center my-3">
                  <h3 className="font_md">สมัครสมาชิก</h3>
                  <p className="">
                    <span className="bg_gd_primary border_10 px-2 me-1">STEP 3</span>
                    ลงทะเบียนชื่อเข้าระบบ (Username)
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="d-grid gap-2">
                  {/* เบอร์โทรศัพท์ (ยืนยันแล้วจาก OTP) — ใช้ล็อกอินได้ทั้งเบอร์หรือ username */}
                  <div className="form_inp">
                    <div className="input_box border_10 mb-2 position-relative">
                      <div className="icon">
                        <i className="fas fa-mobile-alt"></i>
                      </div>
                      <input
                        type="text"
                        value={phone}
                        readOnly
                        placeholder="เบอร์โทรศัพท์"
                        className="c_primary"
                        style={{ color: 'inherit' }}
                        aria-label="เบอร์โทรศัพท์ที่ยืนยันแล้ว"
                      />
                    </div>
                    <h6 className={`c_quinary text-center ${mobileTaken ? 'text-danger' : ''}`}>
                      {mobileTaken ? 'เบอร์โทรศัพท์นี้มีผู้ใช้งานแล้วในระบบ' : 'เบอร์ที่ยืนยัน OTP แล้ว (ใช้ล็อกอินได้ทั้งเบอร์หรือยูสเซอร์)'}
                    </h6>
                  </div>

                  <div className="form_inp">
                    <div className={`input_box border_10 mb-2 position-relative${usernameError ? ' border border-danger' : ''}`}>
                      <div className="icon">
                        <i className="fas fa-user"></i>
                      </div>
                      <input
                        type="text"
                        value={usernameInput}
                        onChange={(e) => {
                          const v = e.target.value.replace(/[^a-zA-Z0-9]/g, '').slice(0, 32)
                          setUsernameInput(v)
                          setUsernameError('')
                        }}
                        readOnly={fromPasscode}
                        disabled={submitting || fromPasscode}
                        placeholder="ยูสเซอร์ผู้ใช้ (ไอดี):"
                        className="c_primary"
                        style={{ color: 'inherit' }}
                      />
                      {fromOtp && isCheckingUsername && (
                        <div className="position-absolute end-0 top-50 translate-middle-y me-3">
                          <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                        </div>
                      )}
                      {fromOtp && !isCheckingUsername && usernameAvailable === true && (
                        <div className="position-absolute end-0 top-50 translate-middle-y me-3">
                          <i className="fas fa-check text-success" />
                        </div>
                      )}
                      {fromOtp && !isCheckingUsername && usernameAvailable === false && (
                        <div className="position-absolute end-0 top-50 translate-middle-y me-3">
                          <i className="fas fa-times text-danger" />
                        </div>
                      )}
                    </div>
                    <h6 className="c_quinary text-center">
                      ยูสเซอร์ (ขั้นต่ำ 6 อักษร ภาษอังกฤษ/ตัวเลขเท่านั้น)
                    </h6>
                    {usernameError && <p className="small c_quinary text-center text-danger mb-0 mt-1">{usernameError}</p>}
                    {fromOtp && showSuggestions && usernameSuggestions.length > 0 && (
                      <div className="mt-2">
                        <small className="c_quinary d-block mb-2">ชื่อผู้ใช้ที่แนะนำ:</small>
                        <div className="d-flex flex-wrap gap-2 justify-content-center">
                          {usernameSuggestions.slice(0, 5).map((s, i) => (
                            <button
                              key={i}
                              type="button"
                              className="btn btn-outline-primary btn-sm border_10"
                              onClick={() => {
                                setUsernameInput(s)
                                setShowSuggestions(false)
                                setUsernameSuggestions([])
                                checkUsernameAvailability(s)
                              }}
                              style={{ fontSize: '0.75rem' }}
                            >
                              {s}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="form_inp">
                    <div className={`input_box border_10 mb-2 position-relative${passwordError ? ' border border-danger' : ''}`}>
                      <div className="icon">
                        <i className="fas fa-key"></i>
                      </div>
                      <input
                        type="password"
                        placeholder="รหัสผ่าน:"
                        value={passwordInput}
                        onChange={(e) => {
                          setPasswordInput(e.target.value)
                          if (passwordError) setPasswordError('')
                          if (confirmPasswordError) setConfirmPasswordError('')
                        }}
                        disabled={submitting || fromPasscode}
                        readOnly={fromPasscode}
                        aria-label={fromPasscode ? 'รหัสผ่านตั้งไว้แล้ว' : 'รหัสผ่าน'}
                      />
                    </div>
                    <h6 className="c_quinary text-center">
                      ขั้นต่ำ 6 ตัวอักษร , ตัวอักษร เล็กใหญ่ ตัวเลข อักขระ
                    </h6>
                    {passwordError && <p className="small c_quinary text-center text-danger mb-0 mt-1">{passwordError}</p>}
                  </div>

                  <div className="form_inp">
                    <div className={`input_box border_10 mb-2 position-relative${confirmPasswordError ? ' border border-danger' : confirmPasswordMatch ? ' border border-success' : ''}`}>
                      <div className="icon">
                        <i className="fas fa-key"></i>
                      </div>
                      <input
                        type="password"
                        placeholder="ยืนยันรหัสผ่าน:"
                        value={confirmPassword}
                        onChange={(e) => {
                          setConfirmPassword(e.target.value)
                          if (confirmPasswordError) setConfirmPasswordError('')
                        }}
                        disabled={submitting || fromPasscode}
                        readOnly={fromPasscode}
                        aria-label={fromPasscode ? 'ยืนยันรหัสผ่านตั้งไว้แล้ว' : 'ยืนยันรหัสผ่าน'}
                      />
                      {confirmPasswordMatch && (
                        <div className="position-absolute end-0 top-50 translate-middle-y me-3">
                          <i className="fas fa-check text-success" />
                        </div>
                      )}
                    </div>
                    <h6 className={`c_quinary text-center ${confirmPasswordError ? 'text-danger' : confirmPasswordMatch ? 'text-success' : ''}`}>
                      {confirmPasswordError ? (
                        confirmPasswordError
                      ) : confirmPasswordMatch ? (
                        <>
                          <i className="fas fa-check-circle me-1" aria-hidden />
                          ยืนยันความถูกต้องของรหัสผ่านแล้ว
                        </>
                      ) : (
                        'กรุณาใส่รหัสผ่านยืนยันอีกครั้งให้เหมือนกัน'
                      )}
                    </h6>
                  </div>

                  <div className="c_tertiary text-center my-3">
                    <h3 className="font_md">ข้อมูลบัญชีธนาคาร</h3>
                  </div>

                  <div className="form_inp">
                    <div className={`input_box border_10 mb-2 position-relative${fullNameError ? ' border border-danger' : ''}`}>
                      <div className="icon">
                        <i className="fas fa-user-plus"></i>
                      </div>
                      <input
                        type="text"
                        placeholder="ชื่อ-นามสกุล"
                        value={fullName}
                        onChange={(e) => {
                          setFullName(e.target.value)
                          if (fullNameError) setFullNameError('')
                        }}
                        disabled={submitting}
                      />
                    </div>
                    <h6 className="c_quinary text-center">
                      กรอกให้ตรงกับชื่อบัญชีของธนาคาร (ข้อมูลนี้ไม่สามารถแก้ไขได้)
                    </h6>
                    {fullNameError && <p className="small c_quinary text-center text-danger mb-0 mt-1">{fullNameError}</p>}
                  </div>

                  <div className="form_inp">
                    <div className={`input_box border_10 mb-2 position-relative${bankError ? ' border border-danger' : ''}`} style={{ display: 'flex', alignItems: 'center' }}>
                      <div className="icon">
                        <i className="fas fa-university"></i>
                      </div>
                      <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center' }}>
                        <Select
                          classNamePrefix="react-select"
                          options={bankOptions}
                          value={bankOptions.find((o) => o.value === bankCode) ?? null}
                          onChange={(opt) => {
                            if (opt) {
                              setBankCode(opt.value)
                              setBankImageUrl(opt.imageUrl || '')
                              if (bankError) setBankError('')
                            }
                          }}
                          isDisabled={submitting || isLoadingBanks}
                          placeholder={isLoadingBanks ? 'กำลังโหลดธนาคาร...' : 'เลือกธนาคาร'}
                          components={{
                            Option: CustomBankOption,
                            SingleValue: CustomBankSingleValue,
                          }}
                          styles={bankSelectStyles}
                        />
                      </div>
                    </div>
                    <h6 className={`c_quinary text-center ${bankAccountTaken ? 'text-danger' : ''}`}>
                      {bankAccountTaken ? (bankAccountCheckMessage || 'เลขบัญชีนี้มีผู้ใช้งานแล้วในระบบ') : 'บัญชีธนาคารเพื่อใช้ถอนเมื่อถูกรางวัล'}
                    </h6>
                    {(bankError || (bankAccountTaken && bankAccountCheckMessage)) && (
                      <p className="small c_quinary text-center text-danger mb-0 mt-1">{bankError || bankAccountCheckMessage}</p>
                    )}
                    {isCheckingBankAccount && (
                      <p className="small c_quinary text-center mb-0 mt-1">กำลังตรวจสอบบัญชีธนาคาร...</p>
                    )}
                  </div>

                  <div className="form_inp">
                    <div className={`input_box border_10 mb-2 position-relative${accountError ? ' border border-danger' : ''}`}>
                      <div className="icon">
                        <i className="fas fa-credit-card"></i>
                      </div>
                      <input
                        type="text"
                        placeholder="หมายเลขบัญชี"
                        value={accountNumber}
                        onChange={(e) => {
                          const v = e.target.value.replace(/\D/g, '').slice(0, 15)
                          setAccountNumber(v)
                          if (accountError) setAccountError('')
                          if (bankError) setBankError('')
                        }}
                        disabled={submitting}
                        maxLength={15}
                        inputMode="numeric"
                      />
                    </div>
                    <h6 className="c_quinary text-center">
                      หมายเลขบัญชี (ตัวเลขเท่านั้น)
                    </h6>
                    {accountError && <p className="small c_quinary text-center text-danger mb-0 mt-1">{accountError}</p>}
                  </div>

                  <button
                    type="submit"
                    className="link_pmr box_row justify-content-center font_rg bg_primary border_10 ins_sd border-0 text-decoration-none"
                    disabled={
                      submitting ||
                      mobileTaken ||
                      (fromOtp && usernameAvailable === false) ||
                      bankAccountTaken ||
                      isCheckingBankAccount
                    }
                  >
                    <span className="c_tertiary">
                      {submitting
                        ? 'กำลังสมัครสมาชิก...'
                        : mobileTaken
                          ? 'เบอร์โทรนี้มีผู้ใช้แล้ว'
                          : bankAccountTaken
                            ? 'เลขบัญชีนี้มีผู้ใช้แล้ว'
                            : (fromOtp && usernameAvailable === false)
                              ? 'ยูสเซอร์นี้มีผู้ใช้แล้ว'
                              : 'ถัดไป →'}
                    </span>
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="bg_show"></div>
    </div>
  )
}

export default ModernRegisterCompletePage
